/**
 * Portal experiences: named presets that each define a whole patient tab strip.
 *
 * Backed by the multivariate LaunchDarkly flag `portal-experience`, which arrives
 * as `portalExperience` via `useFlags()`. Variation values are preset names; the
 * tab list each name maps to lives here, alongside the route wiring it depends on.
 *
 * An unrecognized or missing value resolves to `clinician`, so the LaunchDarkly
 * kill switch, the automated report user, and a flag that does not exist yet all
 * degrade to the intended default rather than to an empty tab strip.
 */

export const PORTAL_EXPERIENCE = Object.freeze({
  clinician: 'clinician',
  legacy: 'legacy',
  legacyPlusClinicianReport: 'legacy-plus-clinician-report',
  internal: 'internal',
})

export const DEFAULT_PORTAL_EXPERIENCE = PORTAL_EXPERIENCE.clinician

/**
 * Tab keys are `ROUTE_METADATA` keys, and array order is tab strip order.
 *
 * `editPatient` is intentionally absent from every preset: the Settings tab is
 * gated on `admin || phiVisibility` from GraphQL membership data, not on the flag.
 * `summaryComparison` is intentionally absent too, being the same tab as
 * `summary` under a second route.
 */
const EXPERIENCE_TAB_KEYS = Object.freeze({
  [PORTAL_EXPERIENCE.clinician]: Object.freeze([
    'overview',
    'daily',
    'log',
    'reportClinician',
  ]),
  [PORTAL_EXPERIENCE.legacy]: Object.freeze([
    'overview',
    'summary',
    'weeklyView',
    'daily',
    'log',
  ]),
  [PORTAL_EXPERIENCE.legacyPlusClinicianReport]: Object.freeze([
    'overview',
    'summary',
    'weeklyView',
    'daily',
    'log',
    'reportClinician',
  ]),
  [PORTAL_EXPERIENCE.internal]: Object.freeze([
    'overview',
    'summary',
    'weeklyView',
    'daily',
    'log',
    'reportPatient',
    'reportClinician',
  ]),
})

/**
 * Normalizes a raw flag value to a known experience.
 *
 * Variation values are typed by hand in the LaunchDarkly dashboard, so surrounding
 * whitespace is tolerated: without this, a trailing space silently degrades the
 * viewer to the default experience with no error anywhere.
 *
 * @param {string} [portalExperience] Raw `portalExperience` flag value
 * @returns {string} One of PORTAL_EXPERIENCE's values
 */
export const resolvePortalExperience = (portalExperience) => {
  const trimmed =
    typeof portalExperience === 'string' ? portalExperience.trim() : ''

  return EXPERIENCE_TAB_KEYS[trimmed] ? trimmed : DEFAULT_PORTAL_EXPERIENCE
}

/**
 * @param {string} [portalExperience] Raw `portalExperience` flag value
 * @returns {string[]} Ordered ROUTE_METADATA keys for the experience's tabs
 */
export const getExperienceTabKeys = (portalExperience) =>
  EXPERIENCE_TAB_KEYS[resolvePortalExperience(portalExperience)]

/**
 * @param {string} [portalExperience] Raw `portalExperience` flag value
 * @param {string} tabKey A ROUTE_METADATA key
 * @returns {boolean} Whether the tab belongs to the experience
 */
export const isTabInExperience = (portalExperience, tabKey) =>
  getExperienceTabKeys(portalExperience).includes(tabKey)
